import { t } from "i18next";
import { Sparkles } from "lucide-react";
import type { JSX } from "react";
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { useSidebar } from "@/components/ui/sidebar/useSidebar";
import { useAuthStore } from "@/store/auth.store";
import { useWhatsNewStore } from "@/store/whatsNew.store";

interface Props {
  className?: string;
  // Label as a tooltip (icon rail).
  tooltip?: boolean;
}

// App menu entry that reopens "What's new". A small dot marks it until the
// announcement has been opened for this version.
export const WhatsNewMenuItem = ({
  className,
  tooltip = false,
}: Props): JSX.Element => {
  const account = useAuthStore((state) => state.userData?.public_id);
  useWhatsNewStore((state) => state.seenVersion);
  const { show, isSeen } = useWhatsNewStore.getState();
  const unread = account !== undefined && !isSeen(account);
  const { isMobile, setOpenMobile } = useSidebar();
  const label = t("sidebar.whatsNew");

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        aria-label={unread ? `${label}, ${t("sidebar.whatsNewUnread")}` : label}
        className={className}
        data-testid="whats-new-menu"
        tooltip={tooltip ? label : undefined}
        type="button"
        onClick={() => {
          // Phones: close the menu drawer so the sheet is not under it.
          if (isMobile) setOpenMobile(false);
          show();
        }}
      >
        {/* Not a span: the icon rail hides direct span children (labels). */}
        <i aria-hidden="true" className="relative inline-flex not-italic">
          <Sparkles className="w-auto! h-6!" />
          {unread && (
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-0.5 size-2.5 rounded-full bg-primary ring-2 ring-sidebar [button:hover_&]:bg-white"
              data-testid="whats-new-dot"
            />
          )}
        </i>
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
